import { useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { shallowEqual, useSelector as useReduxSelector } from 'react-redux';
import { type AnyFieldApi, useForm, useSelector } from '@tanstack/react-form';

import { useAuth } from '@nvision/identity-core';
import { DatePickerField, InputField } from '@nvision/ui';

import {
  type Branch,
  BranchField,
  type CargoType,
  CargoTypeField,
  type Carrier,
  type CarrierCode,
  CarrierField,
  type CarrierService,
  CarrierServiceField,
  type Currency,
  CurrencyField,
  type EquipmentType,
  EquipmentTypeField,
  type LaneRow,
  LaneSearchModal,
  type LinerType,
  LinerTypeField,
  PackageTypeField,
  type PricingType,
  PricingTypeField,
  type TariffType,
  TariffTypeField,
} from '@/modules/entities';
import type { RootState } from '@/redux/RootReducer';
import { getEffectiveDateBounds } from '@/utils/date';

import { normalizeTariffFiltersUpdate } from '../../../data-provider';
import { getTariffsFormDefaultFormValues } from '../../../hooks';
import { type TariffsSearchFiltersFormValues, tariffsSearchFiltersFormSchema } from '../../../models';

import styles from './tariffs-search-filters-form.module.css';

export type FormRef = {
  submit: () => void;
  reset: () => void;
  canSubmit: () => boolean;
};

interface TariffSearchFiltersProps {
  formRef: React.RefObject<FormRef>;
  onSubmit: any;
  initialValues: TariffsSearchFiltersFormValues;
}

export const TariffsSearchFiltersForm: React.FC<TariffSearchFiltersProps> = ({ formRef, onSubmit, initialValues }) => {
  const selectedCompany = useReduxSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);
  const customerKey = selectedCompany?.companyId ?? 0;

  const [isLanesModalOpened, setIsLanesModalOpened] = useState(false);
  const auth = useAuth();
  const timeZone = auth.user?.profile.pref.timezone;

  const form = useForm({
    defaultValues: getTariffsFormDefaultFormValues(timeZone),
    onSubmit: async (props) => {
      if (props.value) {
        onSubmit(normalizeTariffFiltersUpdate(props.value));
      }
    },
    validators: {
      onChange: tariffsSearchFiltersFormSchema,
      onSubmit: tariffsSearchFiltersFormSchema,
    },
  });

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: () => form.reset(),
    submit: () => {
      form.handleSubmit();
    },
  }));

  const onSelectionChange = (lane: LaneRow) => {
    form.setFieldValue('laneKey', {
      id: lane.laneKey,
      label: lane.laneCode || String(lane.laneKey),
    });
    setIsLanesModalOpened(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    console.log('HandleSubmit');
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const resetPackingCode = () => {
    form.setFieldValue('packingCode', null);
  };

  const values = useSelector(form.store, (state) => state.values);
  const selectedCarrierKey = values.carrierKey?.id;
  const selectedCargoTypeId = values.cargoType?.id;

  const { effectiveFromMaxValue, effectiveToMinValue } = useMemo(
    () => getEffectiveDateBounds(values.effectiveDate, values.expirationDate, timeZone),
    [timeZone, values.effectiveDate, values.expirationDate],
  );

  const getError = (field: AnyFieldApi) =>
    field.state.meta.isTouched
      ? field.state.meta.errors
          .map((e: any) => e?.message)
          .filter(Boolean)
          .join('. ')
      : undefined;

  useEffect(() => {
    Object.entries(initialValues).forEach(([key, value]) => {
      form.setFieldValue(key as keyof TariffsSearchFiltersFormValues, value);
    });
  }, [initialValues, form]);

  return (
    <form onSubmit={handleSubmit}>
      <form.Field name="branchKey">
        {(field) => (
          <BranchField
            value={field.state.value as unknown as Branch | null}
            clearable
            onSelectionChange={(branch) =>
              field.handleChange(branch ? { id: Number(branch.id), label: branch.label } : null)
            }
            label="Branch"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="pricingType">
        {(field) => (
          <PricingTypeField
            value={field.state.value as unknown as PricingType | null}
            clearable
            onSelectionChange={(pricingType) =>
              field.handleChange(pricingType ? { id: String(pricingType.id), label: pricingType.label } : null)
            }
            label="Pricing Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="carrierKey">
        {(field) => (
          <CarrierField
            value={field.state.value as unknown as Carrier | null}
            clearable
            defaultFilters={{ customerKey }}
            onSelectionChange={(carrier) => {
              field.handleChange(carrier ? { id: Number(carrier.id), label: carrier.label } : null);
              resetPackingCode();
            }}
            label="Transportation Provider"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="serviceKey">
        {(field) => (
          <CarrierServiceField
            value={field.state.value as unknown as CarrierService | null}
            clearable
            onSelectionChange={(carrierService) =>
              field.handleChange(carrierService ? { id: Number(carrierService.id), label: carrierService.label } : null)
            }
            label="Service"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="currencyCode">
        {(field) => (
          <CurrencyField
            value={field.state.value as unknown as Currency | null}
            clearable
            onSelectionChange={(currency) =>
              field.handleChange(currency ? { id: String(currency.id), label: currency.label } : null)
            }
            label="Currency"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="effectiveDate">
        {(field) => (
          <DatePickerField
            value={field.state.value}
            maxValue={effectiveFromMaxValue}
            onChange={(date) => field.handleChange(date ?? '')}
            label="Effective From"
            size="sm"
            timeZone={timeZone}
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="expirationDate">
        {(field) => (
          <DatePickerField
            value={field.state.value}
            minValue={effectiveToMinValue}
            includeEndOfDay
            onChange={(date) => field.handleChange(date ?? undefined)}
            label="Effective To"
            size="sm"
            timeZone={timeZone}
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="cargoType">
        {(field) => (
          <CargoTypeField
            value={field.state.value as unknown as CargoType | null}
            clearable
            onSelectionChange={(cargoType) => {
              field.handleChange(
                cargoType ? { id: String(cargoType.codeId ?? cargoType.id), label: cargoType.label } : null,
              );
              resetPackingCode();
            }}
            label="Cargo Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="trailerKey">
        {(field) => (
          <EquipmentTypeField
            value={field.state.value as unknown as EquipmentType | null}
            clearable
            onSelectionChange={(equipmentType) =>
              field.handleChange(equipmentType ? { id: Number(equipmentType.id), label: equipmentType.label } : null)
            }
            label="Equipment Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="laneKey">
        {(field) => {
          return (
            <InputField
              value={field.state.value?.label ?? ''}
              clearable
              onChange={(e) => field.handleChange(e.target.value ? field.state.value : null)}
              label="Lane ID"
              placeholder="Enter"
              size="sm"
              errorMessage={getError(field)}
              className={styles.field}
              onClick={() => setIsLanesModalOpened(true)}
            />
          );
        }}
      </form.Field>

      <form.Field name="linerType">
        {(field) => (
          <LinerTypeField
            value={field.state.value as unknown as LinerType | null}
            clearable
            onSelectionChange={(linerType) =>
              field.handleChange(linerType ? { id: String(linerType.id), label: linerType.label } : null)
            }
            label="Liner Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="tariffName">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value || undefined)}
            label="Tariff Name"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="tariffKey">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value || undefined)}
            label="Tariff Key"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="tariffType">
        {(field) => (
          <TariffTypeField
            value={field.state.value as unknown as TariffType | null}
            clearable
            onSelectionChange={(tariffType) =>
              field.handleChange(tariffType ? { id: String(tariffType.id), label: tariffType.label } : null)
            }
            label="Tariff Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="customerLaneId">
        {(field) => (
          <InputField
            value={field.state.value ?? ''}
            clearable
            onChange={(e) => field.handleChange(e.target.value || undefined)}
            label="Customer Lane ID"
            placeholder="Enter"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Field name="packingCode">
        {(field) => (
          <PackageTypeField
            value={field.state.value as unknown as CarrierCode | null}
            clearable
            defaultFilters={{ cargoTypeId: selectedCargoTypeId, carrierKey: selectedCarrierKey }}
            onSelectionChange={(packageType) =>
              field.handleChange(packageType ? { id: String(packageType.id), label: packageType.label } : null)
            }
            label="Package Type"
            size="sm"
            errorMessage={getError(field)}
            className={styles.field}
          />
        )}
      </form.Field>

      <form.Subscribe
        selector={(state) => {
          return [state.canSubmit, state.isSubmitting, state.isPristine];
        }}
      >
        {([_canSubmit]) => (
          <button type="submit" style={{ display: 'none' }}>
            Hidden Submit
          </button>
        )}
      </form.Subscribe>

      <LaneSearchModal
        isOpen={isLanesModalOpened}
        setIsOpen={setIsLanesModalOpened}
        onSelectionChange={onSelectionChange}
      />
    </form>
  );
};
